import { Package, Link2Off } from 'lucide-react'
import type { WireArticleBlock, WireVisualArtifact } from '../../api/types'
import { useVisualPackage } from '../../hooks/useVisualPackage'

// ─── Types ────────────────────────────────────────────────────────────────────

type VisualBlock = Extract<WireArticleBlock, { type: 'visual' }>

// ─── Helpers ──────────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
      {children}
    </p>
  )
}

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary w-20 shrink-0">
        {label}
      </span>
      <span className="text-[11px] text-fg">{value}</span>
    </div>
  )
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded border border-line bg-canvas px-2 py-1 text-xs text-fg placeholder:text-fg-secondary/50 focus:border-accent-primary-line focus:outline-none"
    />
  )
}

// ─── Artifact picker (when no artifact is linked) ─────────────────────────────

function VisualArtifactPicker({
  artifacts,
  onSelect,
}: {
  artifacts: WireVisualArtifact[]
  onSelect: (a: WireVisualArtifact) => void
}) {
  if (artifacts.length === 0) {
    return (
      <div className="rounded border border-dashed border-line px-3 py-4 text-center">
        <Package size={18} className="mx-auto mb-1.5 text-fg-secondary opacity-40" />
        <p className="text-[11px] text-fg-secondary">No .visual packages uploaded yet.</p>
        <p className="mt-0.5 text-[10px] text-fg-secondary opacity-60">
          Upload a .visual file using the Artifacts drawer.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {artifacts.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => onSelect(a)}
          className="flex w-full items-center gap-2 rounded border border-line bg-canvas px-2 py-1.5 text-left transition-colors hover:border-accent-primary-solid hover:bg-fill"
        >
          <Package size={14} className="shrink-0 text-fg-secondary" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[11px] font-medium text-fg">{a.filename}</p>
            <p className="text-[9px] text-fg-secondary opacity-60">
              {(a.fileSize / 1024).toFixed(0)} KB
            </p>
          </div>
          <span className="shrink-0 text-[10px] font-semibold text-accent-primary-fg">
            Link
          </span>
        </button>
      ))}
    </div>
  )
}

// ─── Linked visual info section ───────────────────────────────────────────────

function LinkedVisualInfo({
  artifactId,
  presignedUrl,
  block,
}: {
  artifactId: string
  presignedUrl: string | null
  block: VisualBlock
}) {
  const { data, isLoading } = useVisualPackage(artifactId, presignedUrl)

  if (isLoading) {
    return (
      <div className="space-y-0.5">
        <div className="h-3 w-24 animate-pulse rounded bg-fill" />
        <div className="h-3 w-16 animate-pulse rounded bg-fill" />
      </div>
    )
  }

  const meta = data?.metadata
  return (
    <div className="space-y-0.5 rounded border border-line bg-fill p-2">
      <MetaRow label="Job #" value={block.jobNumber ?? meta?.jobNumber} />
      <MetaRow label="Type" value={meta?.type ?? block.visualType} />
      <MetaRow label="Default" value={meta?.target} />
      {meta?.inlineWidth && (
        <MetaRow label="Width" value={meta.inlineWidth} />
      )}
      {meta?.aspectRatio && (
        <MetaRow label="Ratio" value={String(meta.aspectRatio)} />
      )}
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  block: VisualBlock
  onUpdate: (updated: VisualBlock) => void
  visualArtifacts: WireVisualArtifact[]
}

export function VisualBlockPanel({ block, onUpdate, visualArtifacts }: Props) {
  const artifact = visualArtifacts.find((a) => a.id === block.artifactId)

  function patch(p: Partial<VisualBlock>) {
    onUpdate({ ...block, ...p })
  }

  // ── No artifact linked ────────────────────────────────────────────────────
  if (!block.artifactId) {
    return (
      <div className="space-y-3">
        <div>
          <FieldLabel>Link Visual Package</FieldLabel>
          <VisualArtifactPicker
            artifacts={visualArtifacts}
            onSelect={(a) => patch({ artifactId: a.id })}
          />
        </div>
      </div>
    )
  }

  // ── Artifact linked ───────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* Package info + unlink */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <FieldLabel>Visual Package</FieldLabel>
          <button
            type="button"
            onClick={() => patch({ artifactId: '' })}
            className="flex items-center gap-1 rounded border border-line bg-canvas px-1.5 py-0.5 text-[10px] text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            title="Unlink this artifact"
          >
            <Link2Off size={10} />
            Unlink
          </button>
        </div>
        <LinkedVisualInfo
          artifactId={block.artifactId}
          presignedUrl={artifact?.presignedUrl ?? null}
          block={block}
        />
      </div>

      {/* Display override */}
      <div>
        <FieldLabel>Display Mode</FieldLabel>
        <select
          value={block.displayOverride ?? ''}
          onChange={(e) =>
            patch({
              displayOverride: (e.target.value as VisualBlock['displayOverride']) || null,
            })
          }
          className="w-full rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-accent-primary-line focus:outline-none"
        >
          <option value="">Default (from package)</option>
          <option value="inline">Inline</option>
          <option value="popup">Popup (lightbox)</option>
        </select>
      </div>

      {/* Float override */}
      <div>
        <FieldLabel>Float</FieldLabel>
        <div className="flex gap-1">
          {(['none', 'left', 'right'] as const).map((val) => (
            <button
              key={val}
              type="button"
              onClick={() => patch({ floatOverride: val })}
              className={`flex-1 rounded border px-2 py-0.5 text-[11px] font-semibold capitalize transition-colors ${
                (block.floatOverride ?? 'none') === val
                  ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                  : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
              }`}
            >
              {val === 'left' ? '← Float' : val === 'right' ? 'Float →' : 'None'}
            </button>
          ))}
        </div>
      </div>

      {/* Inline size overrides */}
      <div>
        <FieldLabel>Inline Width Override</FieldLabel>
        <TextInput
          value={block.inlineWidthOverride ?? ''}
          onChange={(v) => patch({ inlineWidthOverride: v || null })}
          placeholder="e.g. 100%, 680px"
        />
      </div>

      <div>
        <FieldLabel>Inline Height Override</FieldLabel>
        <TextInput
          value={block.inlineHeightOverride ?? ''}
          onChange={(v) => patch({ inlineHeightOverride: v || null })}
          placeholder="e.g. 400px"
        />
      </div>

      <div>
        <FieldLabel>Aspect Ratio Override</FieldLabel>
        <input
          type="number"
          value={block.aspectRatioOverride ?? ''}
          onChange={(e) =>
            patch({
              aspectRatioOverride: e.target.value ? parseFloat(e.target.value) : null,
            })
          }
          step="0.01"
          min="0.1"
          placeholder="e.g. 1.77 (16:9)"
          className="w-full rounded border border-line bg-canvas px-2 py-1 text-xs text-fg placeholder:text-fg-secondary/50 focus:border-accent-primary-line focus:outline-none"
        />
      </div>

      {/* Popup/lightbox size overrides */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <FieldLabel>Lightbox Width</FieldLabel>
          <TextInput
            value={block.targetWidthOverride ?? ''}
            onChange={(v) => patch({ targetWidthOverride: v || null })}
            placeholder="80vw"
          />
        </div>
        <div>
          <FieldLabel>Lightbox Height</FieldLabel>
          <TextInput
            value={block.targetHeightOverride ?? ''}
            onChange={(v) => patch({ targetHeightOverride: v || null })}
            placeholder="80vh"
          />
        </div>
      </div>

      {/* Content fields */}
      <div>
        <FieldLabel>Title</FieldLabel>
        <TextInput
          value={block.title ?? ''}
          onChange={(v) => patch({ title: v || null })}
          placeholder="Optional display title"
        />
      </div>

      <div>
        <FieldLabel>Caption</FieldLabel>
        <textarea
          value={block.caption ?? ''}
          onChange={(e) => patch({ caption: e.target.value || null })}
          placeholder="Optional caption text"
          rows={2}
          className="w-full resize-none rounded border border-line bg-canvas px-2 py-1 text-xs text-fg placeholder:text-fg-secondary/50 focus:border-accent-primary-line focus:outline-none"
        />
      </div>

      <div>
        <FieldLabel>Sourcing Line</FieldLabel>
        <TextInput
          value={block.sourcingLine ?? ''}
          onChange={(v) => patch({ sourcingLine: v || null })}
          placeholder="e.g. Source: Reuters"
        />
      </div>

      <div>
        <FieldLabel>Alt Text</FieldLabel>
        <TextInput
          value={block.altText ?? ''}
          onChange={(v) => patch({ altText: v })}
          placeholder="Accessible description"
        />
      </div>

    </div>
  )
}
